import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth, useUser } from '@clerk/clerk-react';
import {
  ArrowLeft,
  CheckCircle,
  CreditCard,
  Home,
  Loader2,
  Medal,
  Target,
  Trophy,
} from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const features = [
  'Access to all ErgShoot programming',
  'Personal workout tracking',
  'Score logging and history',
  'Global leaderboard participation',
  'Mobile-friendly interface',
  'New workouts daily',
];

export default function IndividualSignup() {
  const { isSignedIn } = useUser();
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const handleSubscribe = async () => {
    setIsLoading(true);

    if (!isSignedIn) {
      // Redirect to register first, then come back
      navigate('/register?redirect=/signup/individual');
      return;
    }

    try {
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787/api';
      const token = await getToken();

      // Clerk may have already created a generic member row. Bootstrap the
      // individual profile first, then let its billing route own checkout.
      const roleResponse = await fetch(`${API_URL}/auth/set-role`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          role: 'individual',
        }),
      });

      const roleData = await roleResponse.json();
      if (!roleResponse.ok || !roleData.success) {
        throw new Error(roleData.error || 'Failed to create individual profile');
      }

      const checkoutToken = await getToken({ skipCache: true });
      const checkoutResponse = await fetch(`${API_URL}/individuals/billing/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${checkoutToken}`,
        },
        body: JSON.stringify({}),
      });
      const checkoutData = await checkoutResponse.json();

      if (!checkoutResponse.ok || !checkoutData.success || !checkoutData.data?.checkout_url) {
        throw new Error(checkoutData.error || 'Failed to create checkout session');
      }

      toast.success('Redirecting to payment...');
      window.location.href = checkoutData.data.checkout_url;
    } catch (error) {
      console.error('Error starting checkout:', error);
      toast.error('Failed to start checkout');
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <button
            type="button"
            onClick={() => {
              // BUG-APR08-001: page is often opened in a new tab (target="_blank"
              // from RoleToggleBar), so there's no history to go back to.
              if (window.history.length > 1) navigate(-1);
              else navigate('/');
            }}
            className="inline-flex items-center font-semibold text-primary hover:text-primary/80 transition-colors"
          >
            <ArrowLeft className="mr-2 h-5 w-5" />
            Back
          </button>
          <Link
            to="/"
            className="inline-flex items-center font-semibold text-primary hover:text-primary/80 transition-colors"
          >
            <Home className="mr-2 h-5 w-5" />
            Home
          </Link>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div className="flex justify-center mb-4">
              <img src="/ergshoot-logo.png" alt="ErgShoot" className="h-16 w-auto" />
            </div>
            <h1 className="text-3xl font-bold text-gradient-gold mb-2">Train at Home</h1>
            <p className="text-muted-foreground text-lg">
              Get full access to ErgShoot programming for personal training
            </p>
          </div>

          <div className="grid gap-8 md:grid-cols-2">
            {/* Pricing Card */}
            <Card className="border-primary/50 animate-fade-in">
              <CardHeader className="text-center pb-4">
                <CardTitle className="text-2xl">Individual Plan</CardTitle>
                <CardDescription>Perfect for home athletes</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="text-center">
                  <div className="text-5xl font-bold text-primary">$6.75</div>
                  <p className="text-muted-foreground">/month</p>
                </div>

                <ul className="space-y-3">
                  {features.map((feature, index) => (
                    <li
                      key={feature}
                      className="flex items-center gap-2 text-sm animate-fade-in"
                      style={{ animationDelay: `${index * 50}ms` }}
                    >
                      <CheckCircle className="h-4 w-4 text-green-500 shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>

                <Button onClick={handleSubscribe} disabled={isLoading} className="w-full" size="lg">
                  {isLoading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <CreditCard className="h-4 w-4 mr-2" />
                  )}
                  {isSignedIn ? 'Subscribe Now' : 'Create Account & Subscribe'}
                </Button>

                <p className="text-xs text-center text-muted-foreground">
                  Cancel anytime. No long-term commitment required.
                </p>
              </CardContent>
            </Card>

            {/* What You Get */}
            <div className="space-y-6">
              <Card className="animate-fade-in" style={{ animationDelay: '100ms' }}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Target className="h-5 w-5 text-primary" />
                    Daily Workouts
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Fresh programming every day designed by rowing and fitness experts. Challenge
                    yourself with structured workouts that improve your technique and fitness.
                  </p>
                </CardContent>
              </Card>

              <Card className="animate-fade-in" style={{ animationDelay: '150ms' }}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Medal className="h-5 w-5 text-primary" />
                    Score Tracking
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Log your scores and track your progress over time. See how you improve and set
                    personal records.
                  </p>
                </CardContent>
              </Card>

              <Card className="animate-fade-in" style={{ animationDelay: '200ms' }}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Trophy className="h-5 w-5 text-primary" />
                    Leaderboards
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Compete with athletes worldwide on the global leaderboard. See where you rank
                    and push yourself to improve.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* FAQ */}
          <div className="mt-12 text-center">
            <p className="text-sm text-muted-foreground">
              Questions? Contact us at{' '}
              <a href="mailto:support@ergshoot.com" className="text-primary hover:underline">
                support@ergshoot.com
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
